import type { ActionContext, ActionDependencies } from './types'
import { buildDialogCloseConfigForStore } from '@/common/utils/dialogCloseParentMergeScope'

export const DIALOG_CLOSE_CONFIG_PAGE_KEY = '$dialogCloseConfig'

export type DialogCloseListener = 'dialogClosed' | 'dialogClosedNReturn'

export type DialogClosePhase = 'removeSlot' | DialogCloseListener

export const MAX_DIALOG_CLOSE_CHAIN_DEPTH = 8

/** Swing `Window.close()` then `onDialogClose(incomingData)`. Seed is snapshot, not the live slot. */
export function planDialogClosePhases(listeners: DialogCloseListener[]): DialogClosePhase[] {
  const phases: DialogClosePhase[] = ['removeSlot']
  if (listeners.includes('dialogClosed')) phases.push('dialogClosed')
  if (listeners.includes('dialogClosedNReturn')) phases.push('dialogClosedNReturn')
  return phases
}

/**
 * Overlay for `en.reloadForm()` during dialogClosed.
 * En.getData() stays the child seed. Combined with omitPageFieldSnapshot, POST currentData
 * is empty of both child serialize and the stale parent page snapshot.
 */
export function resolveDialogCloseReloadFormOverlay(_childSerialize?: unknown): Record<string, unknown> {
  return {}
}

type AppStoreLike = {
  topDialog?: { id?: string | number } | null
  dialogIds?: string[]
  pagesData?: Record<string, unknown>
}

export function readDialogCloseConfigFromPage(
  pageData: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  if (!pageData || typeof pageData !== 'object') return {}
  const raw = pageData[DIALOG_CLOSE_CONFIG_PAGE_KEY]
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  return { ...(raw as Record<string, unknown>) }
}

export function resolveDialogCloseConfigForSlot(
  appStore: AppStoreLike,
  slotId: string,
): Record<string, unknown> {
  const normalized = String(slotId ?? '').trim()
  if (!normalized) return {}

  const dialogEntry = (appStore as { dialogs?: Record<string, { config?: unknown }> }).dialogs?.[
    normalized
  ]
  if (dialogEntry?.config && typeof dialogEntry.config === 'object') {
    return { ...(dialogEntry.config as Record<string, unknown>) }
  }

  const pageData = appStore.pagesData?.[normalized] as Record<string, unknown> | undefined
  return readDialogCloseConfigFromPage(pageData)
}

/** Platform dialog only — not a leftover `$parentPageId` document tab (2805). */
export function isPlatformDialogSlot(appStore: AppStoreLike, slotId: string): boolean {
  const normalized = String(slotId ?? '').trim()
  if (!normalized) return false
  if ((appStore.dialogIds ?? []).some((id) => String(id) === normalized)) return true
  const pageData = appStore.pagesData?.[normalized] as Record<string, unknown> | undefined
  return pageData?.$isDialog === true
}

export function isClosableChildWindowSlot(
  appStore: AppStoreLike,
  slotId: string,
): boolean {
  const normalized = String(slotId ?? '').trim()
  if (!normalized) return false

  if (isPlatformDialogSlot(appStore, normalized)) return true

  const pageData = appStore.pagesData?.[normalized] as Record<string, unknown> | undefined
  if (!pageData) return false

  if (pageData.$parentPageId != null && String(pageData.$parentPageId).trim() !== '') {
    return true
  }

  return false
}

export function resolveClosingSlotId(
  context: ActionContext,
  appStore: AppStoreLike,
  resolvePagesDataSlotId: (
    ctx: ActionContext,
    deps?: ActionDependencies,
  ) => string | undefined,
  dependencies?: ActionDependencies,
): string | undefined {
  const actionSlot = String(resolvePagesDataSlotId(context, dependencies) ?? '').trim()
  const formId = context.formId != null ? String(context.formId).trim() : ''

  // Swing getRoot(action).close(): the window that owns the action.
  // Platform dialog OR child window ($parentPageId). Not an arbitrary topDialog
  // (stale after Messages.showMessage). Leftover list-tab guard is closeRoots-only.
  const tryActionRoot = (id: string): string | undefined => {
    if (!id) return undefined
    if (isPlatformDialogSlot(appStore, id) || isClosableChildWindowSlot(appStore, id)) {
      return id
    }
    return undefined
  }

  const fromAction = tryActionRoot(actionSlot) ?? tryActionRoot(formId)
  if (fromAction) return fromAction

  const topDialogId = appStore.topDialog?.id
  if (topDialogId != null) {
    const top = String(topDialogId).trim()
    if (isPlatformDialogSlot(appStore, top)) return top
  }
  return undefined
}

export function attachDialogCloseConfigToPageData(
  pageData: Record<string, unknown>,
  config: Record<string, unknown>,
): Record<string, unknown> {
  if (!config || Object.keys(config).length === 0) return pageData
  return {
    ...pageData,
    [DIALOG_CLOSE_CONFIG_PAGE_KEY]: buildDialogCloseConfigForStore(config),
  }
}

export function isDialogSlot(appStore: AppStoreLike, slotId: string): boolean {
  const normalized = String(slotId ?? '').trim()
  if (!normalized) return false
  return (appStore.dialogIds ?? []).some((id) => String(id) === normalized)
}
